import React, { useEffect } from 'react'
import styled from 'styled-components'
import { Header, Footer } from '@Components/Layout'
import routes from '@Constants/Routes'
import { Navigate, Outlet, Route, useSearchParams } from 'react-router-dom'
import { useLocation, Routes } from 'react-router-dom'
import { useAppSelector } from '@Hooks/ReduxHooks'
import { useStudentScreenSize } from '@eduact/student-theme'
// import Course from '@Features/Course/Pages/Course/Course'
// import Webinar from '@Features/Unit/Webinar/Webinar'
import { LayoutProvider } from '@Providers/LayoutProvider/LayoutProvider'
import { useRouteChrome } from '@Hooks/useRouteChrome'
import ErrorBoundaryFallback from '@Components/ErrorBoundaryFallback/ErrorBoundaryFallback'
import { ErrorBoundary } from 'react-error-boundary'
import NotFoundPage from '@Features/Others/NotFoundPage/NotFoundPage'

import loadable from '@loadable/component'
import LandingPage from '@Features/LandingPage/LandingPage/LandingPage'
import Test from '@Features/Test'
import McqTest from '@Features/Unit/TestMcqUnit/McqTest'
import Dashboard from '@Features/Dashboard'
import Overview from '@Features/Dashboard/Overview'
import ClassroomsOverview from '@Features/Dashboard/Classrooms'
import SingleClassroomOverview from '@Features/Dashboard/Classrooms/Components/Tabs/SingleClassroomOverview'
import SingleClassroomLayout from '@Features/Dashboard/Classrooms/Components/Tabs/SingleClassroomOverview/SingleClassroomLayout/SingleClassroomLayout'
import Profile from '@Features/Dashboard/Profile'
import Tests from '@Features/Dashboard/Classrooms/SingleClassroomOverview/Tests'
import Attendance from '@Features/Dashboard/Classrooms/SingleClassroomOverview/Attendance'

import { redeemImpersonationAction } from '@Features/Authentication/AuthenticationSlice/Authentication.slice'
import { useAppDispatch } from '@Hooks/ReduxHooks'
import { setCurrentURL } from '@Redux/UtilsSlice/UtilsSlice'
import { useDispatch } from 'react-redux'
import Settings from '@Features/Dashboard/Settings'
import AssignmentUnit from '@Features/Unit/AssignmentUnit'
import AppNav from '@Components/Layout/AppNav/AppNav'
import usePlatformFlags from '@Hooks/usePlatformFlags'
const MyClassrooms = loadable(() => import('@Features/MyClassrooms'))
const ActivityHub = loadable(() => import('@Features/ActivityHub'))
const WalletPage = loadable(() => import('@Features/ActivityHub'), { resolveComponent: module => module.WalletPage })
const ForgotPassword = loadable(() => import('@Features/Authentication/ForgotPassword'))
const ResetPassword = loadable(() => import('@Features/Authentication/ResetPassword'))
const SearchResults = loadable(() => import('@Features/LandingPage/SearchResults'))
const SearchPage = loadable(() => import('@Features/Search'))
const Instructor = loadable(() => import('@Features/Instructor'))
const FAQS = loadable(() => import('@Features/Others/FAQs/FAQs'))
const Login = loadable(() => import('@Features/Authentication/Login'))
const VerifyDevice = loadable(() => import('@Features/Authentication/VerifyDevice'))
const About = loadable(() => import('@Features/About'))
const SignUp = loadable(() => import('@Features/Authentication/SignUp'))
const PaymentCallback = loadable(() => import('@Features/Others/PaymentCallback'))
const Classroom = loadable(() => import('@Features/Classroom'))
const ClassroomOverview = loadable(() => import('@Features/Classroom/Pages/ClassroomOverview'))
const Course = loadable(() => import('@Features/Course'))
const CourseDetails = loadable(() => import('@Features/Course/Pages/CourseDetails'))
const Webinar = loadable(() => import('@Features/Unit/Webinar'))
const DocumentUnit = loadable(() => import('@Features/Unit'), { resolveComponent: module => module.DocumentUnit })
const VideoUnit = loadable(() => import('@Features/Unit'), { resolveComponent: module => module.VideoUnit })
const WebContentUnit = loadable(() => import('@Features/Unit'), { resolveComponent: module => module.WebContentUnit })
const Webinars = loadable(() => import('@Features/Dashboard/Classrooms/SingleClassroomOverview/Webinars'))
const StandaloneCourseDetails = loadable(() => import('@Features/StandaloneCourse/Pages/StandaloneCourseDetails'))
const StandaloneLectureDetails = loadable(() => import('@Features/StandaloneCourse/Pages/StandaloneLectureDetails'))
const BundleDetails = loadable(() => import('@Features/Bundle/Pages/BundleDetails'))
// Static pages — local replicas of the abwaab.com footer pages
const AboutUs = loadable(() => import('@Features/StaticPages/AboutUs'))
const OurTeam = loadable(() => import('@Features/StaticPages/OurTeam'))
const ContactUs = loadable(() => import('@Features/StaticPages/ContactUs'))
const TermsOfUse = loadable(() => import('@Features/StaticPages/TermsOfUse'))
const PrivacyPolicy = loadable(() => import('@Features/StaticPages/PrivacyPolicy'))


/**
 * Guests hitting an authed-only URL get a login bounce (with the return URL
 * saved, like the header/classroom CTAs) instead of a 401-driven empty shell.
 */
const RequireAuth: React.FC<{ children: React.ReactElement }> = ({ children }) => {
	const { isAuth, authReady } = useAppSelector(s => s.auth)
	const dispatch = useDispatch()
	const { pathname, search } = useLocation()
	useEffect(() => {
		// Gated on authReady for the same reason as the redirect below: before the
		// answer arrives `isAuth` is false, and writing the return URL here would
		// overwrite wherever the student was actually headed.
		if (authReady && !isAuth) dispatch(setCurrentURL(pathname + search))
	}, [authReady, isAuth, pathname, search, dispatch])
	// Render nothing until /auth/me has answered — the same shape RequirePlatform
	// below uses for its flag. `isAuth` used to be resolvable synchronously from
	// localStorage, so `false` meant "signed out"; it is now the result of a
	// round trip, and `false` also means "haven't asked yet". Without this gate
	// every guarded route bounces a perfectly valid session to /login on each
	// reload, and clobbers the return URL with /login on the way out.
	if (!authReady) return null
	if (!isAuth) return <Navigate to="/login" replace />
	return children
}
/**
 * Platform-mode route gate. Renders nothing while /settings is in flight (no
 * 404 flash on /courses/* deep links — the flag defaults to off), NotFoundPage
 * once loaded-and-disabled, else the children.
 */
const RequirePlatform: React.FC<{ enabled: boolean; children: React.ReactElement }> = ({ enabled, children }) => {
	const { settingsLoaded } = usePlatformFlags()
	if (!settingsLoaded) return null
	if (!enabled) return <NotFoundPage />
	return children
}

/**
 * The recharge flow exists only while the wallet product is on.
 *
 * Sends students to النشاط rather than a 404: the page is being withdrawn, not
 * missing, and old links/bookmarks (plus the legacy /wallet redirect) land
 * here. Waits for /settings so a deep link doesn't bounce on a default.
 */
const RequireWallet: React.FC<{ children: React.ReactElement }> = ({ children }) => {
	const { walletEnabled, settingsLoaded } = usePlatformFlags()
	if (!settingsLoaded) return null
	if (!walletEnabled) return <Navigate to={routes.activity} replace />
	return children
}

/** /courses → the الكورسات tab on استكشف, carrying an optional ?query. */
const CoursesRedirect: React.FC = () => {
	const [params] = useSearchParams()
	const target = new URLSearchParams({ tab: 'courses' })
	const query = params.get('query')
	if (query) target.set('query', query)
	return <Navigate to={`${routes.search}?${target.toString()}`} replace />
}

/** /bundles → the الباقات tab on استكشف, carrying an optional ?query. */
const BundlesRedirect: React.FC = () => {
	const [params] = useSearchParams()
	const target = new URLSearchParams({ tab: 'bundles' })
	const query = params.get('query')
	if (query) target.set('query', query)
	return <Navigate to={`${routes.search}?${target.toString()}`} replace />
}

/**
 * Legacy invoice URLs were bookmarkable with server-side filters (the old
 * TableFilter wrote FILTER_status/from/to into the query string). Map them
 * onto the hub's params instead of dropping them in a fixed-string redirect.
 */
const LegacyInvoicesRedirect: React.FC = () => {
	const [params] = useSearchParams()
	const target = new URLSearchParams({ tab: 'invoices' })
	const status = params.get('FILTER_status') ?? params.get('status')
	if (status) target.set('status', status)
	const from = params.get('from')
	if (from) target.set('from', from)
	const to = params.get('to')
	if (to) target.set('to', to)
	return <Navigate to={`${routes.activity}?${target.toString()}`} replace />
}

const Layout: React.FC<EmbedBaseProps> = ({ embed }) => {
	const { pathname } = useLocation()
	const chrome = useRouteChrome()
	const authChrome = chrome.chrome === 'auth'
	const { isMobile } = useStudentScreenSize()
	// Chrome flags resolve synchronously from the route (ChromeConfig) — no
	// post-mount setter flashes and nothing to reset on unmount.
	const showWallet = !chrome.walletHidden
	const fixedHeader = chrome.fixedHeader !== false
	const showFooter = !(chrome.footerHidden === true || (chrome.footerHidden === 'mobile' && isMobile))
	const { isAuth } = useAppSelector(s => s.auth)
	// The logged-in nav frame: bottom bar on mobile (the desktop row renders
	// inside the Header). Hidden on auth pages, immersive player/test routes,
	// and embedded webviews (their old chrome never had navigation).
	const showAppNav = isAuth && !authChrome && !chrome.navHidden && !embed
	// Typed dispatch: redeemImpersonationAction below is a thunk.
	const dispatch = useAppDispatch()
	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])

	const [params, setParams] = useSearchParams()
	/**
	 * Admin impersonation handoff.
	 *
	 * This used to read `?studentData=` — a JSON blob containing a LIVE student
	 * token — and copy it straight into auth state. That put a working
	 * credential through the iframe URL, and from there into browser history,
	 * Referer headers and access logs. The backend no longer issues such a
	 * token, so the old path could only ever produce a fake authenticated state
	 * the server disagrees with; it is removed rather than kept as a fallback.
	 *
	 * The replacement is a single-use code worth nothing on its own: it is
	 * POSTed for a session cookie and then stripped from the URL, so a refresh
	 * cannot replay a spent one.
	 */
	useEffect(() => {
		const impersonationCode = params.get('impersonationCode')
		if (!impersonationCode) return

		dispatch(redeemImpersonationAction(impersonationCode)).finally(() => {
			const next = new URLSearchParams(params)
			next.delete('impersonationCode')
			setParams(next, { replace: true })
		})
	}, [])
	const { about_us_page, classrooms_enabled, standalone_courses_enabled } = useAppSelector(s => s.settings)
	// Single source of truth for the bundles gate (AND-ed with the courses flag
	// inside the hook) — keep the route and every self-hiding component in sync.
	const { bundlesEnabled } = usePlatformFlags()
	return (
		<StyledLayoutContainer $withNav={showAppNav}>
			<LayoutProvider embed={!!embed} fixedHeader={fixedHeader} showFooter={showFooter} showWallet={showWallet}>
				<ErrorBoundary FallbackComponent={ErrorBoundaryFallback} onReset={() => window.location.reload()}>
					{!authChrome && <Header />}

					<StyledLayoutContent fixedHeader={fixedHeader} noOffset={authChrome}>
						<Routes>
							<Route element={<Outlet />}>
								<Route path={routes.forgotPassword} element={<ForgotPassword />} />
								<Route path={routes.resetPassword} element={<ResetPassword />} />

								{/* Legacy activity URLs → the merged النشاط hub */}
								<Route path={routes.wallet} element={<Navigate to={routes.activityWallet} replace />} />
								<Route path={routes.timeline} element={<Navigate to={`${routes.activity}?tab=events`} replace />} />
								<Route path={routes.invoices} element={<LegacyInvoicesRedirect />} />

								{/* Batch-2 nav destinations — authed only. */}
								<Route path={routes.classrooms} element={<RequireAuth><MyClassrooms /></RequireAuth>} />
								<Route path={routes.activity} element={<RequireAuth><ActivityHub /></RequireAuth>} />
								<Route path={routes.activityWallet} element={<RequireAuth><RequireWallet><WalletPage /></RequireWallet></RequireAuth>} />
								<Route path={routes.account} element={<RequireAuth><Profile /></RequireAuth>} />
								<Route path={routes.accountDevices} element={<RequireAuth><Settings /></RequireAuth>} />

								<Route path={routes.results} element={<SearchResults />} />
							<Route path={routes.search} element={<SearchPage />} />
								<Route path={routes.paymentCallback} element={<PaymentCallback />} />
								<Route path={routes.instructor} element={<Instructor />} />
								<Route path={routes.faqs} element={<FAQS />} />
								{/* abwaab.com footer-page replicas (public, default chrome) */}
								<Route path={routes.aboutUs} element={<AboutUs />} />
								<Route path={routes.ourTeam} element={<OurTeam />} />
								<Route path={routes.contactUs} element={<ContactUs />} />
								<Route path={routes.termsOfUse} element={<TermsOfUse />} />
								<Route path={routes.privacyPolicy} element={<PrivacyPolicy />} />
								{about_us_page && <Route path={routes.about} element={<About />} />}
								<Route path={routes.login} element={<Login />} />
								{/* Deliberately NOT inside RequireAuth: the student IS
								    authenticated here, their browser just is not trusted
								    yet. Gating it would make the challenge unreachable. */}
								<Route path={routes.verifyDevice} element={<VerifyDevice />} />
								{/* <PublicOnlyRoute validator={!isAuth} path={routes.login}>
										</PublicOnlyRoute> */}

								<Route path={`${routes.signup}/*`} element={<SignUp />} />
								<Route path={routes.dashboard} element={<Dashboard />}>
									<Route index element={<Overview />} />
									<Route path={`${routes.dashboard}/profile`} element={<Navigate to={routes.account} replace />} />
									{/* Legacy dashboard-activity URLs → the merged النشاط hub */}
									<Route path={`${routes.dashboard}/activity`} element={<Outlet />}>
										<Route index element={<Navigate to={routes.activity} replace />} />
										<Route path={`timeline-events`} element={<Navigate to={`${routes.activity}?tab=events`} replace />} />
										<Route path={'invoices'} element={<LegacyInvoicesRedirect />} />
										<Route path={`wallet-history`} element={<Navigate to={routes.activityWallet} replace />} />
									</Route>
									<Route path={`${routes.dashboard}/classrooms`} element={<Outlet />}>
										<Route index element={<ClassroomsOverview />} />
										<Route path=":id" element={<SingleClassroomLayout />}>
											<Route index element={<SingleClassroomOverview />} />
											<Route path={'attendance'} element={<Attendance />} />
											<Route path={'webinars'} element={<Webinars />} />
											<Route path={'tests'} element={<Tests />} />
										</Route>
									</Route>
									<Route path={`${routes.dashboard}/settings`} element={<Navigate to={routes.accountDevices} replace />} />
								</Route>
							</Route>

							<Route path={routes.home} element={<LandingPage />} />
							{/* Classic classrooms platform — hidden entirely in courses-only mode. */}
							<Route
								path={routes.classroom}
								element={
									<RequirePlatform enabled={classrooms_enabled}>
										<Classroom />
									</RequirePlatform>
								}
							>
								<Route index element={<ClassroomOverview />} />
								<Route path={routes.courseDetails} element={<CourseDetails />} />
								<Route path={routes.course} element={<Course />}>
									<Route path={routes.documentUnit} element={<DocumentUnit />} />
									<Route path={routes.videoUnit} element={<VideoUnit />} />
									<Route path={routes.webContentUit} element={<WebContentUnit />} />
									<Route path={routes.webinarUnit} element={<Webinar />} />
									<Route path={routes.mcqUnit} element={<McqTest />} />
									<Route path={routes.assignmentUnit} element={<AssignmentUnit />} />
								</Route>
							</Route>
							{/* Standalone courses platform — /courses browses via استكشف;
							    /courses/:label = details; the player nests the SAME unit
							    routes with a standalone parent. */}
							<Route path={routes.coursesCatalog} element={<CoursesRedirect />} />
							<Route
								path={routes.standaloneCourse}
								element={
									<RequirePlatform enabled={standalone_courses_enabled}>
										<Outlet />
									</RequirePlatform>
								}
							>
								<Route index element={<StandaloneCourseDetails />} />
								{/* the lecture page — mirrors the classroom's courses/:cId details */}
								<Route path={routes.courseDetails} element={<StandaloneLectureDetails />} />
								<Route path={routes.course} element={<Course parentType="standalone" />}>
									<Route path={routes.documentUnit} element={<DocumentUnit />} />
									<Route path={routes.videoUnit} element={<VideoUnit />} />
									<Route path={routes.webContentUit} element={<WebContentUnit />} />
									<Route path={routes.webinarUnit} element={<Webinar />} />
									<Route path={routes.mcqUnit} element={<McqTest />} />
									<Route path={routes.assignmentUnit} element={<AssignmentUnit />} />
								</Route>
							</Route>
							{/* Bundles — discounted groups of standalone courses. bundlesEnabled
							    already AND-gates on the courses flag (bundle pages link into
							    /courses/:label). */}
							<Route path={routes.bundlesCatalog} element={<BundlesRedirect />} />
							<Route
								path={routes.bundle}
								element={
									<RequirePlatform enabled={bundlesEnabled}>
										<BundleDetails />
									</RequirePlatform>
								}
							/>
							<Route path={routes.test} element={<Test />} />
							<Route path="*" element={<NotFoundPage />} />
						</Routes>
					</StyledLayoutContent>
				</ErrorBoundary>

				{!authChrome && showFooter && <Footer />}
				{showAppNav && <AppNav variant="bottom" />}
			</LayoutProvider>
		</StyledLayoutContainer>
	)
}

/* When the logged-in bottom nav is present, the whole column (content +
   footer) clears its fixed 64px bar on mobile. */
const StyledLayoutContainer = styled.div<{ $withNav?: boolean }>`
	min-height: 100vh;
	display: flex;
	flex-direction: column;
	${p =>
		p.$withNav &&
		`@media (max-width: 1023px) {
			padding-bottom: var(--bottomnav-height, 64px);
		}`}
`

const StyledLayoutContent = styled.div<{ fixedHeader: boolean; noOffset?: boolean }>`
	/* offset equals the real (measured) fixed-header height — set by Header via
	   --app-header-height — so content sits flush with no gap/overlap. Auth
	   routes mount no Header (the var holds a stale value), so they get 0. */
	margin-top: ${p => (p.noOffset ? '0' : 'var(--app-header-height, 4.938rem)')};
	flex: 1;
	display: flex;
`

export default Layout
